import React, { useState, useEffect } from "react";

const telegraphLogo = new URL("../../assets/Logo-et-horizontal-blanco.svg", import.meta.url).href;

export function LoadingScreen({ onComplete }: { onComplete: () => void }) {
  const [progress, setProgress] = useState(0);
  const [glitchText, setGlitchText] = useState("CÁRCELES");

  useEffect(() => {
    const isMobile = window.matchMedia("(max-width: 767px)").matches;
    const duration = isMobile ? 700 : 2500;
    const interval = 50;
    const steps = duration / interval;
    let currentStep = 0;
    let completionTimer: number | undefined;
    let glitchResetTimer: number | undefined;

    const timer = setInterval(() => {
      currentStep++;
      // Progress calculation with some erratic jumps
      const newProgress = Math.min(100, (currentStep / steps) * 100);
      const randomJump = Math.random() > 0.8 ? Math.random() * 10 : 0;
      setProgress(Math.min(100, newProgress + randomJump));

      // Occasional text glitch
      if (!isMobile && Math.random() > 0.9) {
        const chars = "ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789@#$%&*";
        const glitched = "CÁRCELES"
          .split("")
          .map((c) => (Math.random() > 0.8 ? chars[Math.floor(Math.random() * chars.length)] : c))
          .join("");
        setGlitchText(glitched);
        
        glitchResetTimer = window.setTimeout(() => setGlitchText("CÁRCELES"), 100);
      }

      if (currentStep >= steps) {
        clearInterval(timer);
        setProgress(100);
        setGlitchText("CÁRCELES");
        completionTimer = window.setTimeout(onComplete, isMobile ? 150 : 800);
      }
    }, interval);

    return () => {
      clearInterval(timer);
      if (completionTimer) window.clearTimeout(completionTimer);
      if (glitchResetTimer) window.clearTimeout(glitchResetTimer);
    };
  }, [onComplete]);

  return (
    <div
      className="fixed inset-0 z-[999] flex flex-col items-center justify-center bg-brand-bg text-brand-text overflow-hidden"
    >
      {/* Noise background */}
      <div className="absolute inset-0 z-0 hidden pointer-events-none opacity-20 bg-[radial-gradient(ellipse_at_center,_var(--color-brand-gray-dark)_0%,_transparent_100%)] md:block"></div>
      <div className="absolute inset-0 z-0 hidden pointer-events-none opacity-10 bg-[url('data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSI0IiBoZWlnaHQ9IjQiPgo8cmVjdCB3aWR0aD0iNCIgaGVpZ2h0PSI0IiBmaWxsPSIjZmZmIiBmaWxsLW9wYWNpdHk9IjAuMSIvPgo8L3N2Zz4=')] mix-blend-overlay md:block"></div>
      <div className="loading-scanlines"></div>

      {/* Main Content */}
      <div className="relative z-10 flex flex-col items-center justify-center max-w-3xl px-6 text-center w-full">
        
        <div
          className="loading-panel-enter mb-12"
        >
          <div className="border-y border-brand-border py-4 px-8 relative bg-brand-bg-alt/50">
            <a
              href="https://www.eltelegrafo.com.ec/"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Ir a la portada de El Telégrafo"
            >
              <img
                src={telegraphLogo}
                alt="El Telégrafo"
                className="h-8 md:h-10 w-auto opacity-90 mx-auto"
              />
            </a>
            <div className="absolute top-0 left-0 w-2 h-full bg-brand-neon"></div>
            <div className="absolute top-0 right-0 w-2 h-full bg-brand-neon"></div>
          </div>
          <p className="font-oswald text-xs tracking-widest text-brand-gray mt-4 uppercase">
            Presenta un especial interactivo
          </p>
        </div>

        <div
          className="loading-title-enter flex flex-col items-center"
        >
          <div
            className="loading-glitch font-anton text-6xl md:text-8xl lg:text-9xl uppercase tracking-wider mb-2 text-brand-text"
            data-text={glitchText}
          >
            {glitchText}
          </div>
          
          <div className="flex items-center justify-center gap-4 mb-4 mt-2">
            <div className="h-[1px] w-12 md:w-20 bg-brand-neon"></div>
            <p className="font-caveat text-4xl md:text-5xl text-brand-neon rotate-[-3deg]">
              La escuela del crimen
            </p>
            <div className="h-[1px] w-12 md:w-20 bg-brand-neon"></div>
          </div>
        </div>

        {/* Loading Bar */}
        <div
          className="loading-bar-enter w-full max-w-md mt-24"
        >
          <div className="flex justify-between items-end font-oswald text-xs uppercase tracking-widest text-brand-gray mb-3">
            <span className="flex items-center gap-2">
              <span className="w-2 h-2 rounded-full bg-brand-neon animate-pulse"></span>
              Cargando Expedientes...
            </span>
            <span className="text-brand-neon font-bold text-lg">{Math.floor(progress)}%</span>
          </div>
          <div className="h-1.5 w-full bg-brand-border relative overflow-hidden">
            <div
              className="loading-progress-fill"
              style={{ transform: `scaleX(${progress / 100})` }}
            />
            {/* Grid overlay to make it look like a segmented bar */}
            <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSI0IiBoZWlnaHQ9IjQiPgo8cmVjdCB3aWR0aD0iMSIgaGVpZ2h0PSI0IiBmaWxsPSIjMGEwYTBhIiAvPgo8L3N2Zz4=')]"></div>
          </div>
        </div>
      </div>

      {/* Decorative Corners */}
      <div className="absolute top-6 left-6 w-8 h-8 border-t-2 border-l-2 border-brand-border"></div>
      <div className="absolute top-6 right-6 w-8 h-8 border-t-2 border-r-2 border-brand-border"></div>
      <div className="absolute bottom-6 left-6 w-8 h-8 border-b-2 border-l-2 border-brand-border"></div>
      <div className="absolute bottom-6 right-6 w-8 h-8 border-b-2 border-r-2 border-brand-border"></div>
    </div>
  );
}
